/**
 * ReadingGuide - Reading guide for understanding climate indicators
 * Migrated from front/src/components/ReadingGuide.js
 */
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import ChartContainer from './ChartContainer';
import { PageBreak } from './PrintComponents';
import ReadingGuideReadMore from './ReadingGuideReadMore';
import MarkdownText from './MarkdownText';

// Import example data for demonstration chart
import { entityDataExample } from '../utils/chartsHelpers';
import { usePrint } from '../providers/PrintProvider';
import useReadingGuide from '../hooks/ui/useReadingGuide';

const exampleIndicatorKey = 'R4';

/**
 * ReadingGuide component - Explains how to read and understand climate indicators
 * @param {Object} props - Component props
 * @param {string} props.entityType - Type of entity ('commune' or 'epci')
 * @param {string} props.code - Entity code
 * @param {string} props.name - Entity name
 * @returns {JSX.Element} ReadingGuide component
 */
const ReadingGuide = ({ entityType: _entityType = 'commune', code: _code, name: _name }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { openPrintDialog } = usePrint();
  const [isOpen, setIsOpen] = useState(false);
  const outlook = useSelector(state => state.outlook.value);
  const { getText } = useReadingGuide({ entityType: _entityType });

  return (
    <>
      <Typography id="pourquoi" sx={{ marginTop: '-50px', paddingTop: '50px' }} variant="h5">
        Pourquoi Climadiag Commune ?
      </Typography>

      <MarkdownText>{getText('pourquoi')}</MarkdownText>

      <MarkdownText
        sx={{ '@media print': { display: 'none' } }}
        onLinkClick={() => openPrintDialog({ code: _code, name: _name, outlook })}
      >
        {getText('pourquoi-print')}
      </MarkdownText>

      <Accordion
        expanded={isOpen}
        onChange={() => setIsOpen(!isOpen)}
        elevation={0}
        sx={{ '@media print': { display: 'none' } }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Typography fontWeight={600} color="primary">
            {isOpen ? 'Refermer les détails' : 'Lire la suite'}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <ReadingGuideReadMore />
        </AccordionDetails>
      </Accordion>

      <Box sx={{ display: 'none', '@media print': { display: 'block' } }}>
        <ReadingGuideReadMore />
        <PageBreak />
      </Box>

      <Typography
        variant="h5"
        sx={{
          marginTop: '-50px',
          paddingTop: '50px',
          '@media print': { padding: 0, marginTop: 'unset' }
        }}
        id="figures"
      >
        Comprendre les infographies
      </Typography>

      <MarkdownText>{getText('comprendre')}</MarkdownText>

      <Box sx={{ '@media print': { display: 'none' } }}>
        <ChartContainer
          indicatorKey={exampleIndicatorKey}
          data={entityDataExample}
          isReadingGuide
          showLegendHowToRead={false}
          isSingleSeasonRow={isSmallScreen}
        />
      </Box>

      <Box sx={{ display: 'none', '@media print': { display: 'block' } }}>
        <ChartContainer
          indicatorKey={exampleIndicatorKey}
          data={entityDataExample}
          isReadingGuide
          showLegendHowToRead={false}
          isSingleSeasonRow={isSmallScreen}
          isPrint
        />
      </Box>

      <MarkdownText>{getText('saisons')}</MarkdownText>
      <PageBreak />
    </>
  );
};

ReadingGuide.displayName = 'ReadingGuide';

export default ReadingGuide;
